/**
 * nwsColors · v2.7.38 (2026-02-21)
 * ════════════════════════════════════════════════════════════
 * Authoritative meteorological color matrix mapped to the NWS
 * threat-severity key (https://www.weather.gov/help-map).
 *
 *   Tornado Warning            → Red          (#FF0000)  priority 2
 *   Severe Thunderstorm Warn   → Orange       (#FFA500)  priority 4
 *   Flash Flood Warning        → Vivid Green  (#00FF00)  priority 5
 *   Tornado Watch              → Yellow       (#FFFF00)  priority 45
 *   Severe T-storm Watch       → Pale Violet  (#DB7093)  priority 46
 *   Tornado Emergency          → Magenta/Pink (#FF00FF)
 *   Flash Flood Emergency      → Dark Green   (#006400)
 *   Flood Warning              → Lime         (#84CC16)
 *   Flood Watch                → Sea Green    (#2E8B57)
 *   Hurricane Warning          → Red          (#DC2626)
 *   Winter Storm Warning       → Pink         (#FF69B4)
 *   Ice Storm Warning          → Plum         (#DDA0DD)
 *   Blizzard Warning           → Orange-Red   (#FF4500)
 *   Special Weather Statement  → Cyan         (#06B6D4)
 *   Generic Watch              → Yellow tone
 *   Generic Warning            → Red tone
 *
 * v2.7.38 · Aligned to federal NWS_HAZARD_PALETTES spec (Flash Flood
 * Warning shifted from green→dark red; Tornado Watch tightened from
 * #FFD600 to literal #FFFF00; Severe T-storm Watch shifted from yellow
 * to #DB7093 pale violet red).
 *
 * Each entry returns:
 *   { stroke, fill, name, kind: "warning"|"watch"|"emergency"|"info",
 *     priority: number,
 *     tw: { bg, text, border } }   ← Tailwind class triple for badges
 */

export const NWS_INFO = (event = "", severity = "") => {
  const e = (event || "").toLowerCase();
  const s = (severity || "").toLowerCase();

  // ── Emergencies (the tip-of-the-spear class) ──
  if (e.includes("tornado emergency")) return {
    stroke: "#FF00FF", fill: "#FF00FF", name: "Tornado Emergency", kind: "emergency",
    tw: { bg: "bg-fuchsia-500/20", text: "text-fuchsia-200", border: "border-fuchsia-400/70" },
  };
  if (e.includes("flash flood emergency")) return {
    stroke: "#006400", fill: "#006400", name: "Flash Flood Emergency", kind: "emergency",
    tw: { bg: "bg-green-700/20", text: "text-green-200", border: "border-green-500/70" },
  };

  // ── Warnings (red/orange/dark-red family per NWS spec) ──
  if (e.includes("tornado warning")) return {
    stroke: "#FF0000", fill: "#FF0000", name: "Tornado Warning", kind: "warning", priority: 2,
    tw: { bg: "bg-red-500/20", text: "text-red-100", border: "border-red-400/70" },
  };
  if (e.includes("severe thunderstorm warning")) return {
    stroke: "#FFA500", fill: "#FFA500", name: "Severe Thunderstorm Warning", kind: "warning", priority: 4,
    tw: { bg: "bg-orange-500/20", text: "text-orange-100", border: "border-orange-400/70" },
  };
  if (e.includes("flash flood warning")) return {
    // v2.7.39 · #00FF00 vivid green per refreshed NWS_HAZARD_PALETTES spec
    // (was #8B0000 dark-red in v2.7.38, was #00C853 prior). Decouples
    // flash-flood visuals from red tornado warnings on the dispatch map.
    stroke: "#00FF00", fill: "#00FF00", name: "Flash Flood Warning", kind: "warning", priority: 5,
    tw: { bg: "bg-green-500/20", text: "text-green-100", border: "border-green-400/70" },
  };
  if (e.includes("flood warning")) return {
    stroke: "#84CC16", fill: "#84CC16", name: "Flood Warning", kind: "warning",
    tw: { bg: "bg-lime-500/20", text: "text-lime-100", border: "border-lime-400/70" },
  };
  if (e.includes("hurricane warning") || e.includes("tropical storm warning")) return {
    stroke: "#DC2626", fill: "#DC2626", name: "Hurricane Warning", kind: "warning",
    tw: { bg: "bg-red-600/20", text: "text-red-100", border: "border-red-500/70" },
  };
  if (e.includes("blizzard warning")) return {
    stroke: "#FF4500", fill: "#FF4500", name: "Blizzard Warning", kind: "warning",
    tw: { bg: "bg-orange-600/20", text: "text-orange-100", border: "border-orange-500/70" },
  };
  if (e.includes("ice storm warning")) return {
    stroke: "#DDA0DD", fill: "#DDA0DD", name: "Ice Storm Warning", kind: "warning",
    tw: { bg: "bg-purple-400/20", text: "text-purple-100", border: "border-purple-400/70" },
  };
  if (e.includes("winter storm warning")) return {
    stroke: "#FF69B4", fill: "#FF69B4", name: "Winter Storm Warning", kind: "warning",
    tw: { bg: "bg-pink-500/20", text: "text-pink-100", border: "border-pink-400/70" },
  };

  // ── Watches (per NWS spec — yellow + pale violet) ──
  if (e.includes("tornado watch")) return {
    // v2.7.38 · literal #FFFF00 per federal spec (was #FFD600)
    stroke: "#FFFF00", fill: "#FFFF00", name: "Tornado Watch", kind: "watch", priority: 45,
    tw: { bg: "bg-yellow-400/20", text: "text-yellow-200", border: "border-yellow-400/70" },
  };
  if (e.includes("severe thunderstorm watch")) return {
    // v2.7.38 · #DB7093 pale violet red per federal spec (was #FFE066 yellow)
    stroke: "#DB7093", fill: "#DB7093", name: "Severe Thunderstorm Watch", kind: "watch", priority: 46,
    tw: { bg: "bg-pink-500/20", text: "text-pink-100", border: "border-pink-400/70" },
  };
  if (e.includes("flash flood watch") || e.includes("flood watch")) return {
    stroke: "#2E8B57", fill: "#2E8B57", name: "Flood Watch", kind: "watch",
    tw: { bg: "bg-emerald-600/20", text: "text-emerald-100", border: "border-emerald-400/60" },
  };

  // ── Statements / Advisories ──
  if (e.includes("special weather statement")) return {
    stroke: "#06B6D4", fill: "#06B6D4", name: "Special Weather Statement", kind: "info",
    tw: { bg: "bg-cyan-500/15", text: "text-cyan-200", border: "border-cyan-400/50" },
  };

  // ── Fallback by severity word ──
  if (e.includes("warning") || s === "severe" || s === "extreme") return {
    stroke: "#FF6B6B", fill: "#FF6B6B", name: event || "Warning", kind: "warning",
    tw: { bg: "bg-red-500/15", text: "text-red-300", border: "border-red-400/50" },
  };
  if (e.includes("watch") || s === "moderate") return {
    stroke: "#FACC15", fill: "#FACC15", name: event || "Watch", kind: "watch",
    tw: { bg: "bg-yellow-500/15", text: "text-yellow-300", border: "border-yellow-400/50" },
  };

  // Catch-all
  return {
    stroke: "#A1A1AA", fill: "#A1A1AA", name: event || "Advisory", kind: "info",
    tw: { bg: "bg-zinc-700/30", text: "text-zinc-300", border: "border-zinc-600/60" },
  };
};

// Lightweight tailwind-only badge class — used everywhere a `Badge`
// needs to inherit the NWS color but doesn't want the full info object.
export const nwsBadgeCls = (event, severity) => {
  const i = NWS_INFO(event, severity);
  return `${i.tw.bg} ${i.tw.text} ${i.tw.border}`;
};

// Pure stroke/fill (hex) — for google.maps.Polygon / Circle styling.
export const nwsStroke = (event, severity) => NWS_INFO(event, severity).stroke;
export const nwsFill = (event, severity) => NWS_INFO(event, severity).fill;

// Quick predicate — does this alert raise a WATCH-grade status?
export const isWatch = (event) => /watch/i.test(event || "");
export const isWarning = (event) => /warning|emergency/i.test(event || "");

// Compute the centroid of a GeoJSON geometry (Polygon or MultiPolygon).
// Returns null if the geometry is missing or malformed. Used for placing
// dispatch-map warning triangles at the polygon's center.
export const geometryCentroid = (geom) => {
  if (!geom || !geom.coordinates) return null;
  const rings =
    geom.type === "Polygon" ? [geom.coordinates[0]]
      : geom.type === "MultiPolygon" ? geom.coordinates.map((p) => p[0])
      : null;
  if (!rings) return null;
  let lonSum = 0, latSum = 0, n = 0;
  for (const ring of rings) {
    for (const [lon, lat] of ring) {
      if (typeof lon === "number" && typeof lat === "number") {
        lonSum += lon; latSum += lat; n += 1;
      }
    }
  }
  if (n === 0) return null;
  return { lat: latSum / n, lng: lonSum / n };
};
